import * as React from 'react';

import { autoprefixer } from '../../material-ui/autoprefixer';

import { Rgb } from './utils';

import l10n from '../../util/l10n';

interface RgbSelectorProps extends React.Props<RgbSelector> {
    r: number;
    g: number;
    b: number;
    onChange?: (rgb: Rgb) => void;
}

interface RgbSelectorState {
    r: number;
    g: number;
    b: number;
    focusedKey: 'r' | 'g' | 'b';
    invalidKey: 'r' | 'g' | 'b';
}

export class RgbSelector extends React.Component<RgbSelectorProps, RgbSelectorState> {

    constructor(props: RgbSelectorProps) {
        super(props);

        const { r, g, b } = props;

        this.state = {
            r, g, b,
            focusedKey: null,
            invalidKey: null
        };
    }

    componentWillReceiveProps(nextProps: RgbSelectorProps) {
        const { r, g, b } = this.props;

        if (r !== nextProps.r || g !== nextProps.g || b !== nextProps.b) {
            this.setState({
                r: nextProps.r,
                g: nextProps.g,
                b: nextProps.b
            } as RgbSelectorState);
        }
    }

    render() {
        const styles = this.getStyles();
        const { r, g, b } = this.state;

        /* TODO: Avoid repetition below. Map over `[ 'r', 'g', 'b' ]`. */

        return (
            <div style={styles.root}>
                <label style={styles.label}>
                    {l10n('colorPicker.label.r')}
                    <input
                        style={Object.assign({}, styles.input, { borderColor: this.getInputBorderColor('r') })}
                        value={`${r}`}
                        onChange={(event) => this.onChangeRgb((event.target as HTMLInputElement).value, 'r')}
                        onFocus={() => this.onFocusRgb('r')}
                        onBlur={(event) => this.onBlurRgb((event.target as HTMLInputElement).value, 'r')}
                        data-test-id="color-picker-input-r"
                    />
                </label>
                <label style={styles.label}>
                    {l10n('colorPicker.label.g')}
                    <input
                        style={Object.assign({}, styles.input, { borderColor: this.getInputBorderColor('g') })}
                        value={`${g}`}
                        onChange={(event) => this.onChangeRgb((event.target as HTMLInputElement).value, 'g')}
                        onFocus={() => this.onFocusRgb('g')}
                        onBlur={(event) => this.onBlurRgb((event.target as HTMLInputElement).value, 'g')}
                        data-test-id="color-picker-input-g"
                    />
                </label>
                <label style={styles.label}>
                    {l10n('colorPicker.label.b')}
                    <input
                        style={Object.assign({}, styles.input, { borderColor: this.getInputBorderColor('b') })}
                        value={`${b}`}
                        onChange={(event) => this.onChangeRgb((event.target as HTMLInputElement).value, 'b')}
                        onFocus={() => this.onFocusRgb('b')}
                        onBlur={(event) => this.onBlurRgb((event.target as HTMLInputElement).value, 'b')}
                        data-test-id="color-picker-input-b"
                    />
                </label>
            </div>
        );
    }

    private onChangeRgb = (value: string, key: 'r' | 'g' | 'b') => {
        const n = Number(value);

        if (isNaN(n)) {
            return;
        }

        switch (key) {
            case 'r': this.setState({ r: n } as RgbSelectorState); break;
            case 'g': this.setState({ g: n } as RgbSelectorState); break;
            case 'b': this.setState({ b: n } as RgbSelectorState); break;
            default:
        }

        // If we have a valid `n`,
        // then provide instant feedback.
        if (n >= 0 && n <= 255) {
            if (this.props.onChange) {
                const { r, g, b } = this.state;
                const newRgb = {
                    r: key === 'r' ? n : r,
                    g: key === 'g' ? n : g,
                    b: key === 'b' ? n : b
                };

                this.props.onChange(newRgb);
            }

            this.setState({ invalidKey: null } as RgbSelectorState);
        } else {
            this.setState({ invalidKey: key } as RgbSelectorState);
        }
    };

    private getInputBorderColor = (key: 'r' | 'g' | 'b') => {
        /* TODO: Use colors from some theme. */
        return (
            this.state.invalidKey === key
              ? '#F44336'
              : this.state.focusedKey === key
                  ? '#00BCD4'
                  : '#D9D8D8'
        );
    };

    private onFocusRgb = (key: 'r' | 'g' | 'b') => {
        this.setState({ focusedKey: key } as RgbSelectorState);
    };

    private onBlurRgb = (value: string, key: 'r' | 'g' | 'b') => {
        this.setState({ focusedKey: null } as RgbSelectorState);

        let n = Number(value);
        const { r, g, b } = this.props;

        if (n < 0 || n > 255) {
            switch (key) {
                case 'r': n = r; break;
                case 'g': n = g; break;
                case 'b': n = b; break;
                default:
            }
        }

        const newRgb = {
            r: key === 'r' ? n : r,
            g: key === 'g' ? n : g,
            b: key === 'b' ? n : b
        };

        this.setState({
            r: newRgb.r,
            g: newRgb.g,
            b: newRgb.b,
            invalidKey: null
        } as RgbSelectorState);

        if (this.props.onChange) {
            this.props.onChange(newRgb);
        }
    };

    private getStyles() {
        return {
            root: autoprefixer({
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignContent: 'space-between'
            }),
            label: Object.assign(autoprefixer({
                flexGrow: 1 }), {
                height: 30,
                lineHeight: '30px',
                textAlign: 'right',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginBottom: 12
            }),
            input: {
                width: 60,
                height: 24,
                fontSize: 13,
                fontFamily: 'Open Sans',
                textAlign: 'center',
                border: '1px solid',
                outline: 0,
                marginLeft: 8,
                marginRight: 4,
                padding: 2
            }
        };
    }

}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/components/color-picker/RgbSelector.tsx
 **/